/***
 *
 *   PRODUCT EVENTS
 *   Event analytics with counts by type and feature retention heatmap
 *
 **********/

import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Animate, Badge, Card, Chart, DataTable, Icon, useAPI } from 'components/lib';
import Style from './tailwind';
import {
  MONTH_NAMES, PERIOD_OPTIONS, PERIOD_PREFIX,
  DEFAULT_COHORT_PERIOD, RETENTION_WEEKS,
  EVENT_PLACEHOLDER, COHORT_INFO, HEATMAP_EMPTY_COLOR,
  MSG, CARD_TITLE, LIST_COLUMNS,
} from './constants';
import { DateRangeSelector, eventDetailPath, parseDateOnly, rangeFromSearch, rangeToSearch } from './dateRange';

// ─── Retention Heatmap ──────────────────────────────────────────────────────

function getRetentionCellColor(count, maxCount) {
  if (!count || count === 0) return HEATMAP_EMPTY_COLOR;
  const intensity = Math.min(count / maxCount, 1);
  const r = Math.round(219 - intensity * 160);
  const g = Math.round(234 - intensity * 130);
  const b = Math.round(254 - intensity * 50);
  return `rgb(${r}, ${g}, ${b})`;
}

function formatWeekLabel(dateStr) {
  const d = parseDateOnly(dateStr);
  return `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`;
}

function RetentionTable({ data, loading }) {
  if (loading)               return <div className={Style.stateText}>{MSG.retentionLoading}</div>;
  if (!data || !data.length) return <div className={Style.stateText}>{MSG.retentionEmpty}</div>;

  const allWeeks = [];
  const weekSet  = new Set();
  for (const row of data) {
    for (const w of row.weeks) {
      if (!weekSet.has(w.week_start)) {
        weekSet.add(w.week_start);
        allWeeks.push(w.week_start);
      }
    }
  }
  allWeeks.sort();

  let maxCount = 0;
  for (const row of data) {
    for (const w of row.weeks) {
      if (w.user_count > maxCount) maxCount = w.user_count;
    }
  }

  return (
    <div className={Style.tableScroll}>
      <table className={Style.table}>
        <thead>
          <tr>
            <th className={Style.thStickyWide}>Event</th>
            {allWeeks.map(w => <th key={w} className={Style.thWeek}>{formatWeekLabel(w)}</th>)}
          </tr>
        </thead>
        <tbody>
          {data.map(row => {
            const weekMap = {};
            for (const w of row.weeks) weekMap[w.week_start] = w.user_count;

            return (
              <tr key={row.name}>
                <td className={Style.tdStickyName}>{row.name}</td>
                {allWeeks.map(w => {
                  const count = weekMap[w] || 0;
                  return (
                    <td
                      key={w}
                      className={Style.tdHeatmap}
                      style={{
                        background: getRetentionCellColor(count, maxCount),
                        color: count === 0 ? '#9ca3af' : '#1e3a5f',
                      }}
                    >
                      {count}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Info Tooltip ────────────────────────────────────────────────────────────

function InfoTooltip({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span className={Style.tooltipWrapper}>
      <button
        type="button"
        aria-label="More information"
        aria-expanded={show}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onFocus={() => setShow(true)}
        onBlur={() => setShow(false)}
        className={Style.tooltipIcon}
      >
        i
      </button>
      {show && <div role="tooltip" className={Style.tooltipPopup}>{text}</div>}
    </span>
  );
}

// ─── Cohort Retention Table ──────────────────────────────────────────────────

function formatCohortLabel(dateStr, period) {
  const d = parseDateOnly(dateStr);
  if (period === 'week')  return `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`;
  if (period === 'month') return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
  return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
}

function addPeriods(date, num, period) {
  const d = new Date(date);
  if (period === 'week')       d.setDate(d.getDate() + num * 7);
  else if (period === 'month') d.setMonth(d.getMonth() + num);
  else                         d.setMonth(d.getMonth() + num * 3);
  return d;
}

function CohortRetentionTable({ data, loading, period = 'week' }) {
  if (loading)               return <div className={Style.stateText}>{MSG.cohortLoading}</div>;
  if (!data || !data.length) return <div className={Style.stateText}>{MSG.cohortEmpty}</div>;

  const prefix     = PERIOD_PREFIX[period] ?? 'W';
  const cohorts    = {};
  for (const row of data) {
    if (!cohorts[row.cohort_week]) cohorts[row.cohort_week] = { size: row.cohort_size, weeks: {} };
    cohorts[row.cohort_week].weeks[row.week_num] = row.users;
  }

  const maxPeriod  = Math.max(...data.map(r => r.week_num));
  const cohortKeys = Object.keys(cohorts).sort();
  const today      = new Date();

  return (
    <div className={Style.tableScroll}>
      <table className={Style.table}>
        <thead>
          <tr>
            <th className={Style.thStickyNarrow}>Cohort</th>
            <th className={Style.thUsers}>Users</th>
            {Array.from({ length: maxPeriod + 1 }, (_, i) => (
              <th key={i} className={Style.thPeriod}>
                {i === 0 ? `${prefix}0 (100%)` : `${prefix}${i}`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cohortKeys.map(cw => {
            const cohort     = cohorts[cw];
            const cohortDate = parseDateOnly(cw);

            return (
              <tr key={cw}>
                <td className={Style.tdStickyName}>{formatCohortLabel(cw, period)}</td>
                <td className={Style.tdCount}>{cohort.size}</td>
                {Array.from({ length: maxPeriod + 1 }, (_, periodNum) => {
                  if (addPeriods(cohortDate, periodNum, period) > today) {
                    return <td key={periodNum} className={Style.tdFuture}>—</td>;
                  }

                  const users     = cohort.weeks[periodNum] || 0;
                  const pct       = Math.round((users / cohort.size) * 100);
                  const intensity = pct / 100;

                  return (
                    <td
                      key={periodNum}
                      className={Style.tdHeatmap}
                      style={{
                        background: `rgba(22, 163, 74, ${intensity})`,
                        color: intensity > 0.5 ? '#fff' : '#166534',
                      }}
                    >
                      <div>{pct}%</div>
                      <div className={Style.cohortSubtext}>{users} / {cohort.size}</div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Event Selector ──────────────────────────────────────────────────────────

function EventSelector({ value, onChange, events, placeholder = EVENT_PLACEHOLDER }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref                 = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = (events || []).filter(ev =>
    ev.name.toLowerCase().includes(search.toLowerCase())
  );

  const btnCls = `${Style.btnBase} ${value ? Style.btnActive : Style.btnInactive}`;

  return (
    <div ref={ref} className="relative inline-block">
      <button type="button" onClick={() => setOpen(o => !o)} className={btnCls}>
        {value || placeholder}
        <Icon image="chevron-down" size={12} />
      </button>

      {open && (
        <div className={Style.eventDropdown}>
          <input
            autoFocus
            type="text"
            aria-label={MSG.searchPlaceholder}
            placeholder={MSG.searchPlaceholder}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={Style.searchInput}
          />
          <div className={Style.optionsList}>
            {value && (
              <button
                type="button"
                onClick={() => { onChange(null); setSearch(''); setOpen(false); }}
                className={Style.optionClear}
              >
                {MSG.clearSelection}
              </button>
            )}
            {filtered.length === 0
              ? <span className={Style.optionEmpty}>{MSG.noEvents}</span>
              : filtered.map(ev => (
                  <button
                    type="button"
                    key={ev.name}
                    onClick={() => { onChange(ev.name); setSearch(''); setOpen(false); }}
                    className={`${Style.optionBase} ${value === ev.name ? Style.optionActive : Style.optionInactive}`}
                  >
                    {ev.name}
                  </button>
                ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

// ─── API URL Builders ────────────────────────────────────────────────────────

function buildEventsUrl(start, end) {
  return `/api/product-events?start_date=${start}&end_date=${end}`;
}

function buildChartUrl(name, start, end) {
  return `/api/product-events?name=${encodeURIComponent(name)}&start_date=${start}&end_date=${end}`;
}

function buildCohortUrl(name, period) {
  return `/api/product-events/cohort?name=${encodeURIComponent(name)}&period=${period}`;
}

// ─── Main View ──────────────────────────────────────────────────────────────

const tabCls = (active) => `${Style.btnBase} ${active ? Style.btnActive : Style.btnInactive}`;

function openInNewTab(url) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function ProductEvents() {
  const navigate                        = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // the range lives in the URL so Back from an event drill-down restores it
  const dateRange                       = useMemo(() => rangeFromSearch(searchParams), [searchParams]);
  const setDateRange                    = useCallback(range => setSearchParams(rangeToSearch(range), { replace: true }), [setSearchParams]);
  const [tab, setTab]                   = useState('events');
  const [selectedEvent, setSelected]   = useState(null);
  const [cohortEvent, setCohortEvent]  = useState(null);
  const [cohortPeriod, setCohortPeriod] = useState(DEFAULT_COHORT_PERIOD);

  const events    = useAPI(buildEventsUrl(dateRange.start, dateRange.end));
  const chartData = useAPI(selectedEvent ? buildChartUrl(selectedEvent, dateRange.start, dateRange.end) : null);
  const retention = useAPI(tab === 'retention' ? `/api/product-events/retention?weeks=${RETENTION_WEEKS}` : null);
  const cohort    = useAPI(tab === 'cohort' && cohortEvent ? buildCohortUrl(cohortEvent, cohortPeriod) : null);

  return (
    <Animate>
      <div className={Style.toolbar}>
        <div className={Style.tabGroup}>
          <button type="button" className={tabCls(tab === 'events')}    onClick={() => setTab('events')}>Events</button>
          <button type="button" className={tabCls(tab === 'retention')} onClick={() => setTab('retention')}>Feature Retention</button>
          <button type="button" className={tabCls(tab === 'cohort')}    onClick={() => setTab('cohort')}>Cohort Retention</button>
        </div>
        {tab === 'events' && <DateRangeSelector value={dateRange} onChange={setDateRange} />}
      </div>

      {tab === 'events' && (
        <>
          <div className={Style.eventRow}>
            <EventSelector value={selectedEvent} onChange={setSelected} events={events.data?.results} />
          </div>

          {selectedEvent && chartData.loading && (
            <Card title={`${selectedEvent} events by day`}>
              <div className={Style.stateText}>{MSG.chartLoading}</div>
            </Card>
          )}

          {selectedEvent && chartData.data?.chart && (
            <Card title={`${selectedEvent} events by day`}>
              <Chart type='line' color='blue' data={chartData.data.chart} />
            </Card>
          )}

          <DataTable
            columns={LIST_COLUMNS}
            rows={events.data?.results || []}
            loading={events.loading}
            onRowClick={(row, e) => {
              const url = eventDetailPath(row.name, dateRange);
              if (e.ctrlKey || e.metaKey) openInNewTab(url);
              else navigate(url);
            }}
            onRowAuxClick={row => openInNewTab(eventDetailPath(row.name, dateRange))}
            renderCell={(key, value, row) => {
              if (key === 'name') return <td key={key} className={Style.listCellName}>{value}</td>;
              if (key === 'total_triggers') {
                return <td key={key} className={Style.listCellBadge}><Badge text={value} color="blue" /></td>;
              }
              if (key === 'chart') {
                const active = selectedEvent === row.name;
                return (
                  <td key={key} className={Style.listCellAction}>
                    <button
                      type="button"
                      aria-label={`${MSG.toggleChart}: ${row.name}`}
                      aria-pressed={active}
                      onClick={() => setSelected(active ? null : row.name)}
                      className={`${Style.chartBtn} ${active ? Style.chartBtnActive : ''}`}
                    >
                      <Icon image="bar-chart-2" size={16} />
                    </button>
                  </td>
                );
              }
              return null;
            }}
          />
        </>
      )}

      {tab === 'retention' && (
        <Card title={CARD_TITLE.retention}>
          <RetentionTable data={retention.data?.results} loading={retention.loading} />
        </Card>
      )}

      {tab === 'cohort' && (
        <>
          <div className={Style.cohortControls}>
            <EventSelector value={cohortEvent} onChange={setCohortEvent} events={events.data?.results} />

            <div className={Style.periodToggle}>
              {PERIOD_OPTIONS.map(({ val, label }, i) => (
                <button
                  type="button"
                  key={val}
                  onClick={() => setCohortPeriod(val)}
                  className={`${Style.periodBtnBase} ${i < PERIOD_OPTIONS.length - 1 ? Style.periodBtnDivider : ''} ${cohortPeriod === val ? Style.periodBtnActive : Style.periodBtnIdle}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {!cohortEvent && (
            <Card>
              <div className={Style.emptyCard}>{MSG.cohortPrompt}</div>
            </Card>
          )}

          {cohortEvent && (
            <Card title={
              <span className="inline-flex items-center gap-2">
                {`Cohort Retention \u2014 ${cohortEvent}`}
                <InfoTooltip text={COHORT_INFO} />
              </span>
            }>
              <CohortRetentionTable data={cohort.data} loading={cohort.loading} period={cohortPeriod} />
            </Card>
          )}
        </>
      )}
    </Animate>
  );
}
